import type { RouteObject } from "react-router-dom";
import NotFound from "../pages/NotFound";
import LoginPage from "../pages/login/page";
import ForgotPasswordPage from "../pages/forgot-password/page";
import ResetPasswordPage from "../pages/reset-password/page";
import AccessDeniedPage from "../pages/access-denied/page";
import { AppLayout } from "../layouts/AppLayout";
import { RequireAuth, RequireNav } from "./guards";
import DashboardPage from "../pages/dashboard/page";
import ProfilePage from "../pages/profile/page";
import GalleryPage from "../pages/gallery/page";
import MembersPage from "../pages/members/page";
import MemberFormPage from "../pages/members/form/page";
import MemberProfilePage from "../pages/members/profile/page";
import GroupsPage from "../pages/groups/page";
import CreateGroupPage from "../pages/groups/create/page";
import EditGroupPage from "../pages/groups/edit/page";
import GroupDetailPage from "../pages/groups/detail/page";
import GroupEnrollmentPage from "../pages/groups/enrollment/page";
import AuctionPage from "../pages/auction/page";
import AuctionConsolePage from "../pages/auction/console/page";
import CollectionPage from "../pages/collection/page";
import CollectionSheetPage from "../pages/collection/sheet/page";
import ReceiptEntryPage from "../pages/collection/receipt/page";
import ReceiptPage from "../pages/collection/receipts/page";
import DayBookPage from "../pages/collection/day-book/page";
import LateFeesPage from "../pages/collection/late-fees/page";
import PayoutPage from "../pages/payout/page";
import PayoutProcessPage from "../pages/payout/process/page";
import PayoutVoucherPage from "../pages/payout/voucher/page";
import PayoutHistoryPage from "../pages/payout/history/page";
import ReportsPage from "../pages/reports/page";
import MemberStatementsPage from "../pages/reports/statements/page";
import RawLedgerPage from "../pages/reports/ledger/page";
import CollectionRegisterPage from "../pages/reports/collection-register/page";
import OutstandingReportPage from "../pages/reports/outstanding/page";
import GroupPerformancePage from "../pages/reports/group-performance/page";
import AuctionHistoryPage from "../pages/reports/auction-history/page";
import PayoutRegisterPage from "../pages/reports/payout-register/page";
import EarningsReportPage from "../pages/reports/earnings/page";
// Settings module (Phase 7)
import SettingsPage from "../pages/settings/page";
import BusinessSettingsPage from "../pages/settings/business/page";
import UsersSettingsPage from "../pages/settings/users/page";
import DevicesSettingsPage from "../pages/settings/devices/page";
import PermissionsSettingsPage from "../pages/settings/permissions/page";
import NumberingSettingsPage from "../pages/settings/numbering/page";
import DefaultsSettingsPage from "../pages/settings/defaults/page";
import AuditSettingsPage from "../pages/settings/audit/page";

const routes: RouteObject[] = [
  { path: "/login", element: <LoginPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  { path: "/403", element: <AccessDeniedPage /> },
  {
    path: "/",
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: "profile", element: <ProfilePage /> },
      { path: "gallery", element: <GalleryPage /> },
      // Member Master (Phase 1)
      {
        path: "members",
        element: (
          <RequireNav navKey="members">
            <MembersPage />
          </RequireNav>
        ),
      },
      {
        path: "members/new",
        element: (
          <RequireNav navKey="members">
            <MemberFormPage />
          </RequireNav>
        ),
      },
      {
        path: "members/:id",
        element: (
          <RequireNav navKey="members">
            <MemberProfilePage />
          </RequireNav>
        ),
      },
      {
        path: "members/:id/edit",
        element: (
          <RequireNav navKey="members">
            <MemberFormPage />
          </RequireNav>
        ),
      },
      // Group Management (Phase 2)
      {
        path: "groups",
        element: (
          <RequireNav navKey="groups">
            <GroupsPage />
          </RequireNav>
        ),
      },
      {
        path: "groups/new",
        element: (
          <RequireNav navKey="groups">
            <CreateGroupPage />
          </RequireNav>
        ),
      },
      {
        path: "groups/:id",
        element: (
          <RequireNav navKey="groups">
            <GroupDetailPage />
          </RequireNav>
        ),
      },
      {
        path: "groups/:id/edit",
        element: (
          <RequireNav navKey="groups">
            <EditGroupPage />
          </RequireNav>
        ),
      },
      {
        path: "groups/:id/enrollment",
        element: (
          <RequireNav navKey="groups">
            <GroupEnrollmentPage />
          </RequireNav>
        ),
      },
      // Auction Console (Phase 3)
      {
        path: "auction",
        element: (
          <RequireNav navKey="auction">
            <AuctionPage />
          </RequireNav>
        ),
      },
      {
        path: "auction/:groupId/:cycle",
        element: (
          <RequireNav navKey="auction">
            <AuctionConsolePage />
          </RequireNav>
        ),
      },
      // Cash Collection (Phase 4)
      {
        path: "collection",
        element: (
          <RequireNav navKey="collection">
            <CollectionPage />
          </RequireNav>
        ),
      },
      {
        path: "collection/receipt",
        element: (
          <RequireNav navKey="collection">
            <ReceiptEntryPage />
          </RequireNav>
        ),
      },
      {
        path: "collection/day-book",
        element: (
          <RequireNav navKey="collection">
            <DayBookPage />
          </RequireNav>
        ),
      },
      {
        path: "collection/late-fees",
        element: (
          <RequireNav navKey="collection">
            <LateFeesPage />
          </RequireNav>
        ),
      },
      {
        path: "collection/groups/:groupId",
        element: (
          <RequireNav navKey="collection">
            <CollectionSheetPage />
          </RequireNav>
        ),
      },
      {
        path: "collection/receipts/:receiptId",
        element: (
          <RequireNav navKey="collection">
            <ReceiptPage />
          </RequireNav>
        ),
      },
      // Payout (Phase 5)
      {
        path: "payout",
        element: (
          <RequireNav navKey="payout">
            <PayoutPage />
          </RequireNav>
        ),
      },
      {
        path: "payout/history",
        element: (
          <RequireNav navKey="payout">
            <PayoutHistoryPage />
          </RequireNav>
        ),
      },
      {
        path: "payout/:id",
        element: (
          <RequireNav navKey="payout">
            <PayoutProcessPage />
          </RequireNav>
        ),
      },
      {
        path: "payout/:id/voucher",
        element: (
          <RequireNav navKey="payout">
            <PayoutVoucherPage />
          </RequireNav>
        ),
      },
      // Reports & Ledger (Phase 6)
      {
        path: "reports",
        element: (
          <RequireNav navKey="reports">
            <ReportsPage />
          </RequireNav>
        ),
      },
      {
        path: "reports/statements",
        element: (
          <RequireNav navKey="reports">
            <MemberStatementsPage />
          </RequireNav>
        ),
      },
      {
        path: "reports/ledger",
        element: (
          <RequireNav navKey="reports">
            <RawLedgerPage />
          </RequireNav>
        ),
      },
      {
        path: "reports/collection-register",
        element: (
          <RequireNav navKey="reports">
            <CollectionRegisterPage />
          </RequireNav>
        ),
      },
      {
        path: "reports/outstanding",
        element: (
          <RequireNav navKey="reports">
            <OutstandingReportPage />
          </RequireNav>
        ),
      },
      {
        path: "reports/group-performance",
        element: (
          <RequireNav navKey="reports">
            <GroupPerformancePage />
          </RequireNav>
        ),
      },
      {
        path: "reports/auction-history",
        element: (
          <RequireNav navKey="reports">
            <AuctionHistoryPage />
          </RequireNav>
        ),
      },
      {
        path: "reports/payout-register",
        element: (
          <RequireNav navKey="reports">
            <PayoutRegisterPage />
          </RequireNav>
        ),
      },
      {
        path: "reports/earnings",
        element: (
          <RequireNav navKey="reports">
            <EarningsReportPage />
          </RequireNav>
        ),
      },
      // Settings (Phase 7)
      {
        path: "settings",
        element: (
          <RequireNav navKey="settings">
            <SettingsPage />
          </RequireNav>
        ),
      },
      {
        path: "settings/business",
        element: (
          <RequireNav navKey="settings">
            <BusinessSettingsPage />
          </RequireNav>
        ),
      },
      {
        path: "settings/users",
        element: (
          <RequireNav navKey="settings">
            <UsersSettingsPage />
          </RequireNav>
        ),
      },
      {
        path: "settings/devices",
        element: (
          <RequireNav navKey="settings">
            <DevicesSettingsPage />
          </RequireNav>
        ),
      },
      {
        path: "settings/permissions",
        element: (
          <RequireNav navKey="settings">
            <PermissionsSettingsPage />
          </RequireNav>
        ),
      },
      {
        path: "settings/numbering",
        element: (
          <RequireNav navKey="settings">
            <NumberingSettingsPage />
          </RequireNav>
        ),
      },
      {
        path: "settings/defaults",
        element: (
          <RequireNav navKey="settings">
            <DefaultsSettingsPage />
          </RequireNav>
        ),
      },
      {
        path: "settings/audit",
        element: (
          <RequireNav navKey="settings">
            <AuditSettingsPage />
          </RequireNav>
        ),
      },
    ],
  },
  { path: "*", element: <NotFound /> },
];

export default routes;